import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, distinctUntilChanged, map, Observable, of, tap } from 'rxjs';
import { LoadingState } from '../../interfaces';
import { Filter } from './filter.interface';

export interface ApiResponse<T> {
  data: T;
  meta?: unknown;
}

export interface FilterConfig {
  /** API endpoint to load available filters */
  loadFiltersEndpoint: string;
  /** API endpoint to load filtered options (optional) */
  loadFilteredEndpoint?: string;
  /** API endpoint to load filter labels (optional) */
  loadFilterLabelEndpoint?: string;
  /** Whether to bulk reload filters on initialization */
  bulkReload?: boolean;
}

interface FiltersState {
  loadingState: LoadingState;
  availableFilters: Filter[] | null;
  appliedFilters: Filter[] | null;
}

export interface GlobalFilterState {
  [contextKey: string]: FiltersState;
}

export interface FiltersResponse {
  filters: Filter[];
}

export interface FilterLabelPayload {
  [key: string]: unknown;
}

export interface FilterLabelResponse {
  label: string;
  has_payload: boolean;
  values: unknown[];
}

const INITIAL_CONTEXT_STATE: FiltersState = {
  loadingState: LoadingState.NotSent,
  availableFilters: null,
  appliedFilters: null,
};

/**
 * This is an alternative state service to the ngrx Metrics state module
 * to make this code less coupled to the metrics module
 */
@Injectable({
  providedIn: 'root',
})
export class FiltersService {
  // Dependencies
  private http = inject(HttpClient);

  // Single global state for all filter contexts
  private globalState$ = new BehaviorSubject<GlobalFilterState>({});

  /**
   * Initialize filter management for a specific filter context
   */
  initializeFilters(contextKey: string, config: FilterConfig): void {
    // Ensure context exists in global state
    this.ensureContextExists(contextKey);

    // Auto-load filters if configured to do so
    if (config.bulkReload !== false) {
      this.loadFilters(contextKey, config);
    }
  }

  /**
   * Get filter state observable for a context
   * Safe to call before initializeFilters() - will return default state
   */
  getFilterState$(contextKey: string): Observable<FiltersState> {
    return this.globalState$.pipe(
      map((globalState) => globalState[contextKey] ?? { ...INITIAL_CONTEXT_STATE }),

      distinctUntilChanged()
    );
  }

  /**
   * Get available filters observable
   */
  getAvailableFilters$(contextKey: string): Observable<Filter[]> {
    return this.getFilterState$(contextKey).pipe(
      map((state) => state.availableFilters ?? []),
      distinctUntilChanged()
    );
  }

  /**
   * Get applied filters observable
   */
  getAppliedFilters$(contextKey: string): Observable<Filter[]> {
    return this.getFilterState$(contextKey).pipe(
      map((state) => state.appliedFilters ?? []),
      distinctUntilChanged()
    );
  }

  /**
   * Get loading state observable
   */
  getLoadingState$(contextKey: string): Observable<LoadingState> {
    return this.getFilterState$(contextKey).pipe(
      map((state) => state.loadingState),
      distinctUntilChanged()
    );
  }

  /**
   * Load available filters from API
   */
  private loadFilters(contextKey: string, config: FilterConfig): void {
    this.updateState(contextKey, { loadingState: LoadingState.Pending });

    this.http
      .get<ApiResponse<FiltersResponse>>(config.loadFiltersEndpoint)
      .pipe(
        tap((response) => {
          this.updateState(contextKey, {
            loadingState: LoadingState.Success,
            availableFilters: response.data.filters,
          });

          // Auto-apply initial filters if configured
          if (config.bulkReload && response.data.filters) {
            this.applyInitialFilters(contextKey, response.data.filters);
          }
        }),
        catchError((error) => {
          this.updateState(contextKey, { loadingState: LoadingState.Error });
          return of(null);
        })
      )
      .subscribe();
  }

  /**
   * Add a filter to applied filters
   */
  addFilter(contextKey: string, filter: Filter): void {
    const currentState = this.getCurrentState(contextKey);
    const appliedFilters = currentState.appliedFilters || [];
    const updatedFilters = [...appliedFilters, { ...filter, recently_added: true }];

    this.updateState(contextKey, { appliedFilters: updatedFilters });
  }

  /**
   * Apply a filter (replace existing filter with same key)
   */
  applyFilter(contextKey: string, filter: Filter, config?: FilterConfig): void {
    const currentState = this.getCurrentState(contextKey);
    const appliedFilters = currentState.appliedFilters || [];

    // Remove existing filter with same key
    const filteredAppliedFilters = appliedFilters.filter((f) => f.key !== filter.key);
    const updatedFilters = [...filteredAppliedFilters, filter];

    this.updateState(contextKey, { appliedFilters: updatedFilters });

    // Load filter label if endpoint provided
    if (config?.loadFilterLabelEndpoint && filter.initial_payload) {
      this.loadFilterLabel(contextKey, filter, config);
    }
  }

  /**
   * Remove a filter from applied filters
   */
  removeFilter(contextKey: string, filter: Filter): void {
    const currentState = this.getCurrentState(contextKey);
    const appliedFilters = currentState.appliedFilters || [];
    const updatedFilters = appliedFilters.filter((f) => f.key !== filter.key);

    this.updateState(contextKey, { appliedFilters: updatedFilters });
  }

  /**
   * Clear all applied filters
   */
  clearAllFilters(contextKey: string): void {
    this.updateState(contextKey, { appliedFilters: [] });
  }

  /**
   * Transform applied filters to API payload format
   */
  transformFiltersToPayload(filters: Filter[] | null): Record<string, unknown> {
    if (!filters || filters.length === 0) {
      return {};
    }

    return filters.reduce(
      (acc, filter) => {
        acc[filter.key] = filter.initial_payload;
        return acc;
      },
      {} as Record<string, unknown>
    );
  }

  /**
   * Cleanup filter state for a context
   */
  cleanup(contextKey: string): void {
    const currentGlobalState = this.globalState$.getValue();
    const { [contextKey]: _, ...restState } = currentGlobalState;
    this.globalState$.next(restState);
  }

  private ensureContextExists(contextKey: string): void {
    const currentGlobalState = this.globalState$.getValue();
    if (!currentGlobalState[contextKey]) {
      this.globalState$.next({
        ...currentGlobalState,
        [contextKey]: { ...INITIAL_CONTEXT_STATE },
      });
    }
  }

  private getCurrentState(contextKey: string): FiltersState {
    const globalState = this.globalState$.getValue();
    return globalState[contextKey] ?? { ...INITIAL_CONTEXT_STATE };
  }

  private updateState(contextKey: string, updates: Partial<FiltersState>): void {
    const currentGlobalState = this.globalState$.getValue();
    const currentContextState = currentGlobalState[contextKey] ?? { ...INITIAL_CONTEXT_STATE };
    const newState = {
      ...currentGlobalState,
      [contextKey]: { ...currentContextState, ...updates },
    };

    this.globalState$.next(newState);
  }

  private applyInitialFilters(contextKey: string, filters: Filter[]): void {
    // Use the same logic as the original NGRX implementation to only apply filters
    // that have an initial payload
    const initialFilters = filters.filter((filter) => filter.initial_payload_for_humans?.has_payload);

    if (initialFilters.length > 0) {
      this.updateState(contextKey, { appliedFilters: initialFilters });
    }
  }

  private loadFilterLabel(contextKey: string, filter: Filter, config: FilterConfig): void {
    if (!config.loadFilterLabelEndpoint) return;

    const url = `${config.loadFilterLabelEndpoint}/${filter.key}/humans`;
    const payload = { payload: filter.initial_payload };

    this.http
      .post<ApiResponse<FilterLabelResponse>>(url, payload)
      .pipe(
        tap((response: ApiResponse<FilterLabelResponse>) => {
          const currentState = this.getCurrentState(contextKey);
          const appliedFilters = currentState.appliedFilters || [];

          // Update the filter with human-readable label
          const updatedFilters = appliedFilters.map((f) => {
            if (f.key === filter.key) {
              return {
                ...f,
                initial_payload_for_humans: {
                  label: response.data.label,
                  has_payload: response.data.has_payload,
                  values: response.data.values,
                },
              };
            }
            return f;
          });

          this.updateState(contextKey, { appliedFilters: updatedFilters });
        }),
        catchError((error) => {
          return of(null);
        })
      )
      .subscribe();
  }
}
